import * as React from 'react';

import { BLOCK_CODE, code, MARK_CODE } from '@gitbook/doc-core';
import { Block, Mark, TextRange } from '@gitbook/slate';
import type { Plugin } from '@gitbook/slate-react';
import { ChartSkeleton, ColorCube } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import {
    AnalyticsChartSeriesContext,
    type AnalyticsChartSerieReport,
    SiteAnalyticsQueryChart,
} from '../SiteAnalytics';
import {
    AGENT_CHART_SYNTAX,
    parseAgentChart,
    parseAgentChartSwatch,
    resolveAgentChartSwatch,
} from './agentChart';

const CHART_CLASS_NAME = 'rounded-md border border-base bg-base';

/** Decorates the space between a swatch's code and its figure. */
const MARK_AGENT_CHART_GAP = 'agent-chart-gap';
/** Decorates the figure a swatch's code ties to a chart line, up to its first space. */
const MARK_AGENT_CHART_FIGURE = 'agent-chart-figure';

/** The lines drawn by the charts of the message being rendered, one array per chart. */
const AgentChartSeriesContext = React.createContext<AnalyticsChartSerieReport[][]>([]);

/** Draws the ```chart blocks of a reply. One that is still no chart once the reply is done renders
 * as the code block it is. */
export function createAgentChartPlugin(props: {
    organizationId: string;
    loading: boolean;
}): Plugin {
    const { organizationId, loading } = props;

    return {
        renderNode({ node, attributes, children }) {
            if (
                !Block.isBlock(node) ||
                node.type !== BLOCK_CODE ||
                node.data.get('syntax') !== AGENT_CHART_SYNTAX
            ) {
                return undefined;
            }

            const chart = parseAgentChart(code.getCodeText(node));
            if (!chart && !loading) {
                return undefined;
            }

            return (
                <div {...attributes} className="my-1">
                    {chart ? (
                        <React.Suspense fallback={<ChartSkeleton className={CHART_CLASS_NAME} />}>
                            <SiteAnalyticsQueryChart
                                organizationId={organizationId}
                                siteId={chart.siteId}
                                title={chart.title}
                                query={chart.query}
                            />
                        </React.Suspense>
                    ) : (
                        <ChartSkeleton className={CHART_CLASS_NAME} />
                    )}
                    {/* Slate maps the document onto its text, which the chart stands in for. */}
                    <div hidden>{children}</div>
                </div>
            );
        },
        decorateNode(node) {
            if (!Block.isBlock(node) || !node.isLeafBlock() || !node.text.includes('chart:')) {
                return;
            }

            const decorations: TextRange[] = [];
            node.getTexts().forEach((textNode) => {
                let offset = 0;
                textNode.leaves.forEach((leaf) => {
                    offset += leaf.text.length;
                    const column = leaf.marks.some((mark) => mark.type === MARK_CODE)
                        ? parseAgentChartSwatch(leaf.text)
                        : null;
                    const figure = column ? /^(\s*)(\S+)/.exec(textNode.text.slice(offset)) : null;
                    if (!column || !figure) {
                        return;
                    }

                    const [, gap = '', value = ''] = figure;
                    const decorate = (type: string, start: number, end: number) =>
                        TextRange.create({
                            anchorKey: textNode.key,
                            anchorOffset: start,
                            focusKey: textNode.key,
                            focusOffset: end,
                            marks: [Mark.create({ type, data: { column } })],
                        });
                    const start = offset + gap.length;
                    if (gap) {
                        decorations.push(decorate(MARK_AGENT_CHART_GAP, offset, start));
                    }
                    decorations.push(
                        decorate(MARK_AGENT_CHART_FIGURE, start, start + value.length)
                    );
                });
            });

            return decorations;
        },
        renderMark({ mark, text, node, editor, attributes, children }) {
            // The swatch takes the place of its code and of the space after it.
            if (
                mark.type === MARK_AGENT_CHART_GAP ||
                (mark.type === MARK_CODE && parseAgentChartSwatch(text))
            ) {
                return (
                    <span {...attributes} hidden>
                        {children}
                    </span>
                );
            }

            if (mark.type !== MARK_AGENT_CHART_FIGURE) {
                return undefined;
            }

            return (
                <span {...attributes} className="whitespace-nowrap">
                    <AgentChartSwatch
                        column={mark.data.get('column')}
                        text={editor.value.document.getClosestBlock(node.key)?.text ?? ''}
                    />
                    {children}
                </span>
            );
        },
    };
}

/** Collects the lines the charts of one message draw, for its swatches to take their colours from. */
export function AgentChartSeriesProvider(props: { children: React.ReactNode }) {
    const [charts, setCharts] = React.useState<ReadonlyMap<string, AnalyticsChartSerieReport[]>>(
        () => new Map()
    );
    const report = useEventCallback(
        (chartId: string, series: AnalyticsChartSerieReport[] | null) => {
            setCharts((previous) => {
                const next = new Map(previous);
                if (series) {
                    next.set(chartId, series);
                } else {
                    next.delete(chartId);
                }
                return next;
            });
        }
    );
    const drawn = React.useMemo(() => [...charts.values()], [charts]);

    return (
        <AnalyticsChartSeriesContext.Provider value={report}>
            <AgentChartSeriesContext.Provider value={drawn}>
                {props.children}
            </AgentChartSeriesContext.Provider>
        </AnalyticsChartSeriesContext.Provider>
    );
}

/** The legend square of the line a figure is about, or nothing while that is in any doubt. */
function AgentChartSwatch(props: { column: string; text: string }) {
    const { column, text } = props;
    const charts = React.useContext(AgentChartSeriesContext);
    const color = resolveAgentChartSwatch({ charts, column, text });

    return color ? <ColorCube color={color} size={10} className="mr-1" /> : null;
}
